import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';

const FAQS = [
  {
    category: 'Orders & Accounts',
    items: [
      {
        q: 'How do I create an account?',
        a: 'Click "Sign Up" in the top navigation and enter your name and email. Once you\'re in, you can shop, track orders, and access your personal referral code.',
      },
      {
        q: 'How do I place an order?',
        a: 'Browse the collection, add items to your cart, and proceed through checkout. Any referral cash on your account can be applied at checkout (shipping is still charged). We accept all major credit cards via Stripe.',
      },
      {
        q: 'Can I cancel or modify an order after placing it?',
        a: 'Orders can be cancelled within 1 hour of placement if they have not yet been packed. Email us at support@ab8beauty.com or call 714-334-7127 right away with your order number. Once packed, cancellations are not possible.',
      },
    ],
  },
  {
    category: 'Shipping & Delivery',
    items: [
      {
        q: 'How much is shipping?',
        a: 'Shipping is free on orders over $50. Below that, a flat shipping rate is calculated at checkout. Orders are processed on business days (Monday–Friday, excluding holidays).',
      },
      {
        q: 'How long does delivery take?',
        a: 'Standard delivery is 3–7 business days. Express (1–3 business days) is available at checkout for an additional fee. Tracking information is emailed when your order ships.',
      },
      {
        q: 'Do you ship internationally?',
        a: 'We currently ship within the United States. We\'re working on expanding — join the glow list to hear when international shipping goes live.',
      },
    ],
  },
  {
    category: 'Products & Skin',
    items: [
      {
        q: 'What makes AB8 Beauty products different?',
        a: 'Our formulas pair science-backed actives — GHK-CU copper peptides and bee venom — with ethically sourced, boho-luxe botanicals. They\'re designed to rejuvenate, smooth, and restore skin\'s youthful radiance, naturally.',
      },
      {
        q: 'Are your products clean and cruelty-free?',
        a: 'Yes. Every AB8 Beauty product is clean, vegan, and never tested on animals. We use ethically sourced botanicals and modern, skin-loving formulations.',
      },
      {
        q: 'Should I patch test?',
        a: 'We recommend a patch test before first use, especially with our bee venom and active peptide formulas. Apply a small amount to your inner forearm and wait 24 hours. Discontinue use if irritation occurs.',
      },
      {
        q: 'How soon will I see results?',
        a: 'Skincare is a ritual. Many customers notice softer, more hydrated skin right away, with firmer, more radiant results building over several weeks of consistent use.',
      },
    ],
  },
  {
    category: 'Referral Rewards',
    items: [
      {
        q: 'How does the referral program work?',
        a: 'Share your referral code with friends. When they order, you earn referral cash across three tiers of referrals — credited to your account and redeemable 1:1 at checkout (shipping is still charged).',
      },
      {
        q: 'When does referral cash become available?',
        a: 'Referral cash is added to your account once a referred order has shipped — no long holding period.',
      },
      {
        q: 'Is there a minimum or expiry on referral cash?',
        a: 'No. Referral cash has no minimum redemption amount and does not expire. Apply it at checkout anytime you have a balance. Shipping is never covered by referral cash.',
      },
    ],
  },
];

function Accordion({ items }) {
  const [open, setOpen] = useState(null);
  return (
    <div className="divide-y divide-gray-200 border border-gray-200 rounded-xl overflow-hidden">
      {items.map((item, i) => (
        <div key={i}>
          <button
            className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-gray-50 transition-colors focus:outline-none focus-visible:bg-gray-50"
            onClick={() => setOpen(open === i ? null : i)}
            aria-expanded={open === i}
          >
            <span className="font-semibold text-gray-900 pr-4">{item.q}</span>
            <svg
              className={`w-5 h-5 text-tea-600 shrink-0 transition-transform duration-200 ${open === i ? 'rotate-180' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {open === i && (
            <div className="px-6 pb-5 text-gray-600 leading-relaxed text-[15px]">
              {item.a}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function SupportPage() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />

      {/* Hero */}
      <section className="bg-tea-700 text-white py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-tea-300 text-sm font-semibold uppercase tracking-widest mb-4">Help & Support</p>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight leading-tight">
            How can we help?
          </h1>
          <p className="mt-4 text-tea-200 text-lg max-w-xl">
            Answers to common questions about orders, shipping, our products, and referral rewards.
          </p>
        </div>
      </section>

      {/* Contact bar */}
      <div className="bg-tea-50 border-b border-tea-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-wrap items-center gap-6 text-sm">
          <div className="flex items-center gap-2 text-tea-800">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
            </svg>
            <a href="mailto:support@ab8beauty.com" className="font-medium hover:underline">
              support@ab8beauty.com
            </a>
          </div>
          <a href="tel:+17143347127" className="font-medium text-tea-800 hover:underline">714-334-7127</a>
          <span className="text-tea-600">Response within 1 business day · Mon–Fri 9 AM–5 PM PT</span>
        </div>
      </div>

      {/* FAQ */}
      <main className="flex-1 py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col gap-14">
          {FAQS.map((section) => (
            <div key={section.category}>
              <h2 className="text-xl font-bold text-gray-900 mb-5">{section.category}</h2>
              <Accordion items={section.items} />
            </div>
          ))}

          {/* Still have questions */}
          <div className="bg-tea-50 rounded-2xl p-8 text-center">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Still have questions?</h3>
            <p className="text-gray-600 mb-5">
              Our support team typically responds within one business day.
            </p>
            <a
              href="mailto:support@ab8beauty.com"
              className="btn-primary"
            >
              Email Support
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
